// Manage Account — the participant "control panel" page (route "/manage"). Thin
// assembly layer: reads data through the module hook, handles loading / error /
// empty, and composes the prototype's layout from the manage-account module's
// feature widgets (each built from the reusable library). Visual design matches
// the PCS v3 prototype.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageHead, SectionCard, Surface, Skeleton, ErrorState, EmptyState,
  // HIDDEN (Ask Risa, 2026-09-23): GenieTrigger,
} from '../../components/reusable';
import {
  useManageAccountData,
  AccountHero,
  ContributionsCard,
  DirectionsCard,
  RebalanceCard,
  TransferCard,
  PlanLineup,
} from '../../components/modules/manage-account';
// HIDDEN (Ask Risa, 2026-09-23): the page-head trigger was this hook's only consumer here.
// import { useRisa } from '../../components/modules/risa';
// The avatar + profile dropdown, relocated out of the removed top navbar (FM-2406).
// HIDDEN (FM-2714): the participant avatar + profile dropdown. Kept for later — restore
// by uncommenting this import and the <AccountMenu /> element in the page head.
// import AccountMenu from '../../components/header/AccountMenu';
import { formatDate } from '../../utils/format';
import { useLegacyPortal } from '../../hooks';

// Loading skeleton mirrors the real layout so it doesn't jump on load.
function ManageSkeleton() {
  return (
    <>
      <Surface padding="lg"><Skeleton variant="title" /><Skeleton variant="text" count={2} /></Surface>
      <Surface><Skeleton variant="title" /><Skeleton variant="block" height={200} /></Surface>
      <Surface><Skeleton variant="title" /><Skeleton variant="block" height={160} /></Surface>
    </>
  );
}

function Manage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useManageAccountData();
  // HIDDEN (Ask Risa, 2026-09-23): const { isOpen: risaOpen, toggle: toggleRisa } = useRisa();
  // Exclusive accordion (phones ≤600px); ignored by ProcessCard on larger screens.
  const [openKey, setOpenKey] = useState(null);
  const acc = (key) => ({
    open: openKey === key,
    onToggle: () => setOpenKey((k) => (k === key ? null : key)),
  });

  // The four levers hand off to the legacy portal, which owns the write flows. Each
  // names its own ROUTE KEY: only `contributions` has a confirmed legacy path today —
  // the other three land on legacy Manage Account until the .NET team supplies
  // theirs, at which point config/legacyPortal.js is the only file that changes.
  const { openLegacy } = useLegacyPortal();

  return (
    <div className="ma-page" data-screen-label="Manage Account">
      <PageHead
        trail={[t('manage.trail')]}
        onNavigate={(crumb) => crumb?.to && navigate(crumb.to)}
        action={(
          <>
            {/* HIDDEN (Ask Risa, 2026-09-23): <GenieTrigger onClick={() => toggleRisa()} open={risaOpen} /> */}
            {/* HIDDEN (FM-2714): <AccountMenu /> */}
          </>
        )}
      />

      {loading && <ManageSkeleton />}

      {!loading && error && (
        <Surface>
          <ErrorState message={t('manage.loadError')} onRetry={refetch} />
        </Surface>
      )}

      {!loading && !error && !data && (
        <Surface>
          <EmptyState title={t('manage.emptyTitle')} message={t('manage.emptyMessage')} />
        </Surface>
      )}

      {!loading && !error && data && (
        <>
          <AccountHero account={data.account} />

          <div className="ma-card-grid">
            <ContributionsCard contrib={data.contributions} pay={data.pay} onAction={() => openLegacy('contributions')} {...acc('contrib')} />
            <DirectionsCard
              allocations={data.allocations}
              rules={data.electionRules}
              perPaycheck={data.contributions.totalPerCheck}
              onAction={() => openLegacy('investments')}
              {...acc('dir')}
            />
            <RebalanceCard rebalance={data.rebalance} allocations={data.allocations} onAction={() => openLegacy('rebalance')} {...acc('rebal')} />
            <TransferCard transfer={data.transfer} onAction={() => openLegacy('transfers')} {...acc('xfer')} />
          </div>

          <SectionCard headerless className="ma-lineup-section">
            <PlanLineup funds={data.planFunds} heldTickers={data.heldTickers} asOfLabel={data.account.asOf ? formatDate(data.account.asOf) : undefined} />
          </SectionCard>
        </>
      )}
    </div>
  );
}

export default Manage;
